import React, { useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Form,
  InputGroup,
  Modal,
  Spinner,
  Table,
} from "react-bootstrap";
import { FaEdit, FaEye, FaPlus, FaSearch, FaTrashAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { couponsAPI } from "../../api/apiClient";

const INITIAL_FORM = {
  code: "",
  discount_percentage: "",
  max_uses: "",
  expires_at: "",
};

export default function Coupons() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [coupons, setCoupons] = useState([]);
  const [filteredCoupons, setFilteredCoupons] = useState([]);
  const [usages, setUsages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState("");
  const [form, setForm] = useState(INITIAL_FORM);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showUsagesModal, setShowUsagesModal] = useState(false);
  const [loadingUsages, setLoadingUsages] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState(null);

  useEffect(() => {
    fetchCoupons();
  }, []);

  useEffect(() => {
    const normalizedSearch = searchTerm.trim().toUpperCase();
    if (!normalizedSearch) {
      setFilteredCoupons(coupons);
      return;
    }

    setFilteredCoupons(
      coupons.filter((coupon) =>
        coupon.code?.toUpperCase().includes(normalizedSearch),
      ),
    );
  }, [coupons, searchTerm]);

  const fetchCoupons = async () => {
    setLoading(true);
    setError("");
    const response = await couponsAPI.index();

    if (response.success) {
      const items = response.data.data || [];
      setCoupons(items);
      setFilteredCoupons(items);
    } else {
      setError(response.message || t("coupons.fetch_failed"));
    }

    setLoading(false);
  };

  const openCreateModal = () => {
    setEditingCoupon(null);
    setForm(INITIAL_FORM);
    setShowFormModal(true);
  };

  const openEditModal = (coupon) => {
    setEditingCoupon(coupon);
    setForm({
      code: coupon.code || "",
      discount_percentage: coupon.discount_percentage ?? "",
      max_uses: coupon.max_uses ?? "",
      expires_at: coupon.expires_at ? String(coupon.expires_at).slice(0, 10) : "",
    });
    setShowFormModal(true);
  };

  const closeFormModal = () => {
    setShowFormModal(false);
    setEditingCoupon(null);
    setForm(INITIAL_FORM);
  };

  const handleFormChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: field === "code" ? value.toUpperCase() : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");

    const payload = {
      code: form.code.trim().toUpperCase(),
      discount_percentage: Number(form.discount_percentage),
      max_uses: Number(form.max_uses),
      expires_at: form.expires_at || null,
    };

    const response = editingCoupon
      ? await couponsAPI.update(editingCoupon.id, payload)
      : await couponsAPI.store(payload);

    if (!response.success) {
      setError(response.message || t("coupons.save_failed"));
      setSaving(false);
      return;
    }

    closeFormModal();
    await fetchCoupons();
    setSaving(false);
  };

  const handleDelete = async (coupon) => {
    const confirmed = window.confirm(
      t("coupons.confirm_delete", { code: coupon.code }),
    );
    if (!confirmed) return;

    const response = await couponsAPI.delete(coupon.id);
    if (!response.success) {
      setError(response.message || t("coupons.delete_failed"));
      return;
    }

    await fetchCoupons();
  };

  const openUsagesModal = async (coupon) => {
    setSelectedCoupon(coupon);
    setShowUsagesModal(true);
    setLoadingUsages(true);

    const response = await couponsAPI.usages(coupon.id);
    if (response.success) {
      setUsages(response.data.data || []);
    } else {
      setError(response.message || t("coupons.usages_fetch_failed"));
      setUsages([]);
    }

    setLoadingUsages(false);
  };

  const renderStatus = (status) => (
    <Badge bg={status === "active" ? "success" : "danger"}>
      {status === "active" ? "Active" : "Expired"}
    </Badge>
  );

  return (
    <div className="py-4">
      <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
        <h3 className="fw-bold mb-3">{t("coupons.title")}</h3>
        <Button onClick={openCreateModal}>
          <FaPlus className="me-2" />
          {t("coupons.add")}
        </Button>
      </div>

      <InputGroup className="mb-3">
        <InputGroup.Text>
          <FaSearch />
        </InputGroup.Text>
        <Form.Control
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          placeholder={t("coupons.search_placeholder")}
        />
      </InputGroup>

      {error && <Alert variant="danger">{error}</Alert>}

      <div className="table-responsive bg-white rounded shadow-sm">
        <Table bordered hover className="mb-0 text-center align-middle">
          <thead className="table-dark">
            <tr>
              <th>{t("coupons.code")}</th>
              <th>{t("coupons.discount_percentage")}</th>
              <th>{t("coupons.max_uses")}</th>
              <th>{t("coupons.used_count")}</th>
              <th>{t("coupons.expires_at")}</th>
              <th>{t("coupons.status")}</th>
              <th>{t("coupons.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="py-4">
                  <Spinner animation="border" />
                </td>
              </tr>
            ) : filteredCoupons.length === 0 ? (
              <tr>
                <td colSpan={7} className="py-4">
                  {t("coupons.no_results")}
                </td>
              </tr>
            ) : (
              filteredCoupons.map((coupon) => (
                <tr key={coupon.id}>
                  <td className="fw-bold">{coupon.code}</td>
                  <td>{coupon.discount_percentage}%</td>
                  <td>{coupon.max_uses}</td>
                  <td>{coupon.used_count}</td>
                  <td>{coupon.expires_at ? String(coupon.expires_at).slice(0, 10) : "-"}</td>
                  <td>{renderStatus(coupon.status)}</td>
                  <td>
                    <Button
                      variant="outline-primary"
                      className="me-2 mb-1"
                      onClick={() => openUsagesModal(coupon)}
                    >
                      <FaEye />
                    </Button>
                    <Button
                      variant="outline-warning"
                      className="me-2 mb-1"
                      onClick={() => openEditModal(coupon)}
                    >
                      <FaEdit />
                    </Button>
                    <Button
                      variant="outline-danger"
                      className="mb-1"
                      disabled={coupon.used_count > 0}
                      onClick={() => handleDelete(coupon)}
                    >
                      <FaTrashAlt />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      <Modal show={showFormModal} onHide={closeFormModal} centered>
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              {editingCoupon ? t("coupons.edit") : t("coupons.add")}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>{t("coupons.code")}</Form.Label>
              <Form.Control
                required
                value={form.code}
                onChange={(event) => handleFormChange("code", event.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("coupons.discount_percentage")}</Form.Label>
              <Form.Control
                type="number"
                min="1"
                max="100"
                required
                value={form.discount_percentage}
                onChange={(event) => handleFormChange("discount_percentage", event.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("coupons.max_uses")}</Form.Label>
              <Form.Control
                type="number"
                min="1"
                required
                value={form.max_uses}
                onChange={(event) => handleFormChange("max_uses", event.target.value)}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>{t("coupons.expires_at")}</Form.Label>
              <Form.Control
                type="date"
                value={form.expires_at}
                onChange={(event) => handleFormChange("expires_at", event.target.value)}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeFormModal}>
              {t("coupons.cancel")}
            </Button>
            <Button type="submit" disabled={saving}>
              {saving
                ? t("coupons.saving")
                : editingCoupon
                  ? t("coupons.update")
                  : t("coupons.create")}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal
        show={showUsagesModal}
        onHide={() => setShowUsagesModal(false)}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {t("coupons.usages_title")}
            {selectedCoupon ? ` - ${selectedCoupon.code}` : ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="table-responsive">
            <Table bordered hover className="text-center align-middle">
              <thead className="table-light">
                <tr>
                  <th>{t("coupons.order_ref")}</th>
                  <th>{t("coupons.order_date")}</th>
                  <th>{t("coupons.order_status")}</th>
                  <th>{t("coupons.original_price")}</th>
                  <th>{t("coupons.discounted_price")}</th>
                  <th>{t("coupons.customer_name")}</th>
                  <th>{t("coupons.customer_email")}</th>
                  <th>{t("coupons.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {loadingUsages ? (
                  <tr>
                    <td colSpan={8} className="py-4">
                      <Spinner animation="border" />
                    </td>
                  </tr>
                ) : usages.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="py-4">
                      {t("coupons.no_usages")}
                    </td>
                  </tr>
                ) : (
                  usages.map((usage) => (
                    <tr key={usage.id}>
                      <td>{usage.order?.reference_number || t("coupons.order_deleted")}</td>
                      <td>{usage.order?.date || t("coupons.order_deleted")}</td>
                      <td>{usage.order?.status || t("coupons.order_deleted")}</td>
                      <td>{usage.original_price}</td>
                      <td>{usage.discounted_price}</td>
                      <td>{usage.user?.name || t("coupons.customer_deleted")}</td>
                      <td>{usage.user?.email || t("coupons.customer_deleted")}</td>
                      <td>
                        <Button
                          variant="outline-primary"
                          className="me-2 mb-1"
                          disabled={!usage.order_id}
                          onClick={() => navigate(`/admin/bookings/details/${usage.order_id}`)}
                        >
                          {t("order_details")}
                        </Button>
                        <Button
                          variant="outline-secondary"
                          className="mb-1"
                          disabled={!usage.user_id}
                          onClick={() => navigate(`/admin/customers/details/${usage.user_id}`)}
                        >
                          {t("coupons.customer_details")}
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}
